import React, { useEffect } from 'react'
import Modal from '../../../components/Modal/Modal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { Button, TabMenu } from '@demandlocal/components'
import { useNavigate } from 'react-router'
import DealershipInfo from './Routes/DealershipInfo'
import DealershipIntegrations from './Routes/DealershipIntegrations'
import { useGetDealershipById } from '../../../api/admin/useGetDealershipById'
import states from '../../../lib/utils/states'
import toast from 'react-hot-toast'
import { useEditDealership } from '../../../api/admin/useEditDealership'
import DealershipPremiumFeatures from './Routes/DealershipPremiumFeatures'
import countries from '../../../lib/utils/countries'
import { capitalize, omit } from 'lodash'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { useGetTagCodes } from '../../../api/utils/useGetTagCodes'
import { useCreateVendorConnectionForDealership } from '../../../api/admin/useCreateVendorConnectionForDealership'
import { VENDOR } from '../../../lib/utils/vendors'
import { queryClient } from '../../../api/queryClient'

export const IndustryOptions = [
  {
    label: 'Automotive',
    value: 'automotive',
  },
  {
    label: 'Retail',
    value: 'retail',
  },
]

export default function EditDealership(): JSX.Element {
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const dealership = useDealership()

  const { mutateAsync } = useEditDealership()
  const { mutateAsync: createVendorConnection } = useCreateVendorConnectionForDealership()
  const navigate = useNavigate()

  const { dealership_id } = useParams()
  const { data: tagCodes } = useGetTagCodes({ pageSize: 100 })
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, errors, isDirty },
  } = formContext

  const { data, isLoading } = useGetDealershipById(dealership_id)

  useEffect(() => {
    if (!isLoading) {
      reset({
        ...data,
        industry: IndustryOptions.find((i) => i.value === data?.industry),
        state: states.filter((state) => data?.state?.includes(state.value)),
        country: countries.find((country) => data?.country?.includes(country.value)) || {
          label: 'USA',
          value: 'USA',
        },
        attribution_type: data?.attribution_tracking?.type && {
          value: data?.attribution_tracking?.type,
          label: `${capitalize(
            data?.attribution_tracking?.type?.replace('_', ' '),
          )} date`,
        },
        attribution_goals: dealership?.attribution_tracking?.goals?.map((id) => {
          const tag = tagCodes?.data?.find((t) => t.id === id)
          return {
            label: `${tag?.tag_name} [${tag?.tag_code}]`,
            value: id,
          }
        }),
        ttd_advertiser_id: data?.ttd_account?.value,
        ttd_secret_key: data?.ttd_account?.access_token,
      })
    }
  }, [data, dealership?.attribution_tracking?.goals, isLoading, reset, tagCodes?.data])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Saving...')
    try {
      await mutateAsync({
        id: dealership_id,
        data: {
          dealership: values?.dealership,
          address: values?.address,
          city: values?.city,
          state: values?.state?.value,
          country: values?.country?.value,
          zip: values?.zip,
          email: values?.email,
          phone: values?.phone,
          url: values?.url,
          client_id: values?.client_id,
          industry: values?.industry?.value,
          organizations: values?.organizations,
          elead_subscription: values?.elead_subscription,
          vin_solutions_dealership: values?.vin_solutions_dealership,
          cdk_fi_sales_subscription: values?.cdk_fi_sales_subscription,
          dealersocket_dealership_id: values?.dealersocket_dealership_id,
          tekion_dealership_id: values?.tekion_dealership_id,
          drivecentric_dealership_id: values?.drivecentric_dealership_id,
          procede_dealership_id: values?.procede_dealership_id,
          promax_dealership_id: values?.promax_dealership_id,
          amazon_ads_account_id: values?.amazon_ads_account_id,
          enable_audience_download: values?.enable_audience_download,
          enable_path_to_conversion_report:
            values?.enable_path_to_conversion_report ?? false,
          enable_path_to_conversion_user_list:
            values?.enable_path_to_conversion_user_list ?? false,
          enable_path_to_sale_report: values?.enable_path_to_sale_report ?? false,
          enable_polk_report: values?.enable_polk_report ?? false,
          attribution_tracking:
            values?.attribution_goals?.length && values?.attribution_type?.value
              ? {
                  type: values?.attribution_type?.value,
                  goals: values?.attribution_goals
                    ?.map((d) => d.value)
                    ?.filter((id) => id > 0),
                }
              : undefined,
          polk_partners: values?.polk_partners?.map((d) => omit(d, 'customDetail')),
          polk_competitors: values?.polk_competitors?.map((d) => omit(d, 'customDetail')),
          enable_ai_insights: values?.enable_ai_insights ?? false,
          enable_conversion_report: values?.enable_conversion_report ?? false,
          enable_creative_report: values?.enable_creative_report ?? false,
          enable_ga4_report: values?.enable_ga4_report ?? false,
          self_service: values?.self_service ?? false,
          performance_overview: values?.performance_overview ?? false,
        },
      })
      if (
        values?.ttd_secret_key &&
        values?.ttd_advertiser_id &&
        !values?.ttd_secret_key?.includes(':')
      ) {
        await createVendorConnection({
          data: {
            dealership_id: data?.id,
            vendor_id: VENDOR.TradeDesk,
            value: values?.ttd_advertiser_id,
            label: values?.ttd_advertiser_id,
            access_token: values?.ttd_secret_key,
          },
        })
      }
      await queryClient.invalidateQueries(['admin', 'dealerships'])
      toast.success('Saved!', { id: toastId })
      navigate('../')
    } catch (err) {
      toast.error('Unable to save dealership at this time', {
        id: toastId,
        duration: 5000,
      })
    }
  }
  return (
    <Modal modalRoute={true} isOpen={true} key={'AdminUserAdd'}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 flex-col h-screen'}>
          <div
            className={
              'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
            }>
            <div className={'text-3xl font-bold mt-12'}>Edit {data?.dealership}</div>
            <div className={'pt-12 pb-2'}>
              <TabMenu
                options={[
                  { name: 'Details', to: 'details' },
                  {
                    name: 'Integrations',
                    to: 'integrations',
                    hasError: !!errors.organizations,
                  },
                  {
                    name: 'Premium Features',
                    to: 'premium',
                  },
                ]}
              />
            </div>
            <div className={'font-Spartan overflow-scroll h-full'}>
              <Routes>
                <Route
                  path={'details'}
                  element={<DealershipInfo formContext={formContext} />}
                />
                <Route path={'integrations'} element={<DealershipIntegrations />} />
                <Route path={'premium'} element={<DealershipPremiumFeatures />} />
                <Route path={'*'} element={<Navigate to={'details'} />} />
              </Routes>
            </div>
            <div className={'flex justify-between py-5 border-t-2'}>
              <Button
                disabled={isSubmitting}
                variant={'secondary'}
                animate={false}
                type={'button'}
                onClick={() => {
                  if (isDirty) {
                    confirm(
                      'Are you sure you want to leave this page? All unsaved changes will be lost.',
                    )
                    {
                      navigate('../')
                    }
                  } else {
                    navigate('../')
                  }
                }}>
                Cancel
              </Button>
              <Button animate={false} disabled={isSubmitting} variant={'primary'}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
